import React from 'react';
import { useTranslation } from 'react-i18next';
import V2Icon from './V2Icon';

type LayoutMode = 'desktop' | 'portable' | 'ultra';

const MODE_ORDER: LayoutMode[] = ['desktop', 'portable', 'ultra'];

function DesktopMockup() {
  return (
    <div className="faq-layout-mock faq-layout-mock--desktop" aria-hidden="true">
      <div className="faq-layout-mock__sidebar">
        <span className="faq-layout-mock__logo" />
        <span className="faq-layout-mock__nav faq-layout-mock__nav--active" />
        <span className="faq-layout-mock__nav" />
        <span className="faq-layout-mock__nav" />
      </div>
      <div className="faq-layout-mock__workspace">
        <div className="faq-layout-mock__header">
          <span className="faq-layout-mock__crumb" />
          <span className="faq-layout-mock__search" />
          <span className="faq-layout-mock__tool">
            <V2Icon name="dice" />
          </span>
          <span className="faq-layout-mock__tool">
            <V2Icon name="autopaste" />
          </span>
          <span className="faq-layout-mock__tool">
            <V2Icon name="compact" />
          </span>
        </div>
        <div className="faq-layout-mock__body">
          <span className="faq-layout-mock__kpi" />
          <span className="faq-layout-mock__chart" />
          <span className="faq-layout-mock__panel" />
        </div>
      </div>
    </div>
  );
}

function PortableMockup() {
  return (
    <div className="faq-layout-mock faq-layout-mock--portable" aria-hidden="true">
      <div className="faq-layout-mock__toolbar">
        <span className="faq-layout-mock__zoom">− +</span>
        <span className="faq-layout-mock__search faq-layout-mock__search--compact" />
        <span className="faq-layout-mock__tool faq-layout-mock__tool--active">
          <V2Icon name="compact" />
        </span>
      </div>
      <div className="faq-layout-mock__body faq-layout-mock__body--portable">
        <span className="faq-layout-mock__player" />
        <span className="faq-layout-mock__tabs" />
        <span className="faq-layout-mock__preflop" />
        <span className="faq-layout-mock__panel faq-layout-mock__panel--short" />
      </div>
    </div>
  );
}

function UltraMockup() {
  const { t } = useTranslation('translation');

  return (
    <div className="faq-layout-mock faq-layout-mock--ultra" aria-hidden="true">
      <div className="faq-layout-mock__toolbar faq-layout-mock__toolbar--ultra">
        <span className="faq-layout-mock__player faq-layout-mock__player--short" />
        <span className="faq-layout-mock__ultra-btn faq-layout-mock__ultra-btn--active">Ultra</span>
        <span className="faq-layout-mock__ultra-btn">{t('faq_layout_ultra_style_btn')}</span>
      </div>
      <div className="faq-layout-mock__ultra-grid">
        {Array.from({ length: 8 }, (_, index) => (
          <span key={index} className="faq-layout-mock__ultra-cell" />
        ))}
      </div>
    </div>
  );
}

const MOCKUPS: Record<LayoutMode, React.FC> = {
  desktop: DesktopMockup,
  portable: PortableMockup,
  ultra: UltraMockup,
};

const ULTRA_DETAIL_SECTIONS = [
  { titleKey: 'faq_layout_ultra_section_stats', stepsKey: 'faq_layout_ultra_stats_steps' },
  { titleKey: 'faq_layout_ultra_section_style', stepsKey: 'faq_layout_ultra_style_steps' },
  { titleKey: 'faq_layout_ultra_section_rename', stepsKey: 'faq_layout_ultra_rename_steps' },
  { titleKey: 'faq_layout_ultra_section_hud', stepsKey: 'faq_layout_ultra_hud_steps' },
] as const;

function renderSteps(steps: string[]) {
  return (
    <ol className="faq-layout-card__steps">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}

const V2FAQLayoutGuide: React.FC = () => {
  const { t } = useTranslation('translation');

  return (
    <div className="faq-layout-guide">
      {MODE_ORDER.map((mode) => {
        const Mockup = MOCKUPS[mode];
        const titleKey = `faq_layout_${mode}_title` as const;
        const bodyKey = `faq_layout_${mode}_body` as const;
        const steps = t(`faq_layout_${mode}_steps`, { returnObjects: true }) as string[];

        return (
          <article key={mode} className={`faq-layout-card faq-layout-card--${mode}`}>
            <div className="faq-layout-card__visual">
              <Mockup />
            </div>
            <div className="faq-layout-card__copy">
              <h3>{t(titleKey)}</h3>
              <p className="faq-layout-card__lead">{t(bodyKey)}</p>
              {Array.isArray(steps) && steps.length > 0 ? renderSteps(steps) : null}
              {mode === 'ultra'
                ? ULTRA_DETAIL_SECTIONS.map(({ titleKey: sectionTitleKey, stepsKey }) => {
                    const sectionSteps = t(stepsKey, { returnObjects: true }) as string[];
                    if (!Array.isArray(sectionSteps) || sectionSteps.length === 0) {
                      return null;
                    }

                    return (
                      <div key={stepsKey} className="faq-layout-card__subsection">
                        <h4>{t(sectionTitleKey)}</h4>
                        {renderSteps(sectionSteps)}
                      </div>
                    );
                  })
                : null}
            </div>
          </article>
        );
      })}
      <aside className="faq-layout-note">
        <h4>{t('faq_layout_responsive_title')}</h4>
        <p>{t('faq_layout_responsive_body')}</p>
      </aside>
    </div>
  );
};

export default V2FAQLayoutGuide;
